import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import { isTesting } from "@embroider/macros";
import type { ComponentLike } from "@glint/template";
import type CnamServicesAccessFormAuthenticationModifyPracticeLocationComponent from "parog-web/components/cnam-services-access-form/authentication/modify-practice-location";
import type CnamServicesAccessFormAuthenticationSetPinCodeAndComputerSettingsComponent from "parog-web/components/cnam-services-access-form/authentication/set-pin-code-and-computer-settings";
import type CnamServicesAccessFormAuthenticationSetPracticeLocationComponent from "parog-web/components/cnam-services-access-form/authentication/set-practice-location";
import type InsDocuments from "parog-web/components/cnam-services-access-form/ins-documents";
import RetrieveAndSaveDocuments from "parog-web/components/cnam-services-access-form/process/loading";
import ProcessSuccess from "parog-web/components/cnam-services-access-form/process/success";
import type RateExceeded from "parog-web/components/cnam-services-access-form/rate-exceeded";
import type AuthorizationState from "parog-web/components/dmp-retrieve-access-form/authorization-state";
import type SetSecretConnection from "parog-web/components/dmp-retrieve-access-form/set-secret-connection";
import type DocumentsRetrieveAccessFormShowComponent from "parog-web/components/documents-retrieve-access-form/show";
import type InsRetrieveAccessFormConfirmIdentityComponent from "parog-web/components/ins-retrieve-access-form/confirm-identity";
import type CnamProcessStartedJournalEvent from "parog-web/models/cnam-process-started-journal-event";
import type Patient from "parog-web/models/patient";
import TimedTransaction from "parog-web/models/timed-transaction";
import { CnamApiError } from "parog-web/utils/cnam-api-errors";
import ControlTower from "wise-control-tower";

export type OperationType = "dmp" | "insi";
type StateComponent =
  | typeof RetrieveAndSaveDocuments
  | typeof InsDocuments
  | typeof RateExceeded
  | typeof AuthorizationState
  | typeof InsRetrieveAccessFormConfirmIdentityComponent
  | typeof CnamServicesAccessFormAuthenticationSetPinCodeAndComputerSettingsComponent
  | typeof DocumentsRetrieveAccessFormShowComponent
  | typeof CnamServicesAccessFormAuthenticationModifyPracticeLocationComponent
  | typeof CnamServicesAccessFormAuthenticationSetPracticeLocationComponent
  | typeof SetSecretConnection;

export class State {
  declare isUserActionRequired: boolean;

  constructor(
    public key: string,
    public shouldShowState: () => Promise<boolean>,
    public run: () => Promise<void>,
    public component?: StateComponent,
    public loadingComponent?: typeof RetrieveAndSaveDocuments
  ) {
    if (component) {
      this.isUserActionRequired = true;
    } else {
      this.component = RetrieveAndSaveDocuments;
      this.isUserActionRequired = false;
    }
    this.loadingComponent = loadingComponent || RetrieveAndSaveDocuments;
  }
}

export default abstract class CnamContext {
  @tracked declare currentState?: State;
  @tracked declare currentTimedTransaction: TimedTransaction;
  @tracked isLoading = false;
  @tracked declare lastError?: Error;
  @tracked declare successKey?: string;
  @tracked declare timedTransactions: TimedTransaction[];

  // Subclasses (e.g. DmpRetrievalContext) override this with a component
  // requiring a more specific `context` type, so this is kept wide.
  declare cnamProcessStartedJournalEvent: CnamProcessStartedJournalEvent;
  declare onError?: () => void;
  declare states: State[];
  successComponent: ComponentLike<any> = ProcessSuccess;

  constructor(
    public store: Store,
    public requestManager,
    public cnamAdapterName: string,
    public patient?: Patient,
    public operationType?: OperationType
  ) {
    this.states = operationType
      ? [
          new State(
            "save-cnam-process-started",
            (): Promise<boolean> => {
              return Promise.resolve(!this.cnamProcessStartedJournalEvent);
            },
            async (): Promise<void> => {
              this.cnamProcessStartedJournalEvent = this.store.createRecord(
                "cnam-process-started-journal-event",
                {
                  cnamAdapterName,
                  operationType,
                  occurredAt: new Date()
                }
              );
              if (this.patient && this.patient.isNew) {
                /*
                 * If the patient is not created or persisted yet save the JE without patient
                 * Then associate the patient to use the relationship later in the process
                 * to persist the relationship in db
                 */
                await this.cnamProcessStartedJournalEvent.save();
                this.cnamProcessStartedJournalEvent.patient = this.patient;
              } else {
                /* Otherwise associated the patient and save the JE directly with the
                 * patient relationship
                 */
                this.cnamProcessStartedJournalEvent.patient = this
                  .patient as Patient;
                await this.cnamProcessStartedJournalEvent.save();
              }
            }
          )
        ]
      : [];
  }

  _transitionTo = async (): Promise<void> => {
    try {
      if (this.currentState) {
        this.currentTimedTransaction = new TimedTransaction(
          this.currentState?.key,
          new Date()
        );

        await this.currentState?.run();

        this.currentTimedTransaction.endDate = new Date();
        this.currentTimedTransaction.success = true;
        this.timedTransactions.push(this.currentTimedTransaction);
      }

      this.currentState = await this.calculateCurrentState();

      if (
        this.currentState &&
        !this.currentState?.isUserActionRequired &&
        !this.successKey
      ) {
        await this._transitionTo();
      }
    } catch (error) {
      if (!isTesting()) console.error("Error in context", error);

      if (error instanceof CnamApiError && !isTesting()) {
        console.error("Details", error.error);

        ControlTower.captureException(error);
      }

      this.lastError = error;

      this.currentTimedTransaction.endDate = new Date();
      this.currentTimedTransaction.success = false;
      this.currentTimedTransaction.error = {
        name: error.name,
        message: error.message
      };
      this.timedTransactions.push(this.currentTimedTransaction);

      await this.store
        .createRecord("cnam-process-ended-journal-event", {
          timedTransactions: this.timedTransactions,
          occurredAt: new Date(),
          patient: this.patient?.isNew ? null : this.patient,
          cnamProcessStartedJournalEvent: this.cnamProcessStartedJournalEvent
        })
        .save();

      this.onError?.();
    }
  };

  async calculateCurrentState(): Promise<State | undefined> {
    for (const state of this.states) {
      if (await state.shouldShowState()) {
        return state;
      }
    }
    return undefined;
  }

  reset(): void {
    this.lastError = undefined;
    this.successKey = undefined;
  }

  transitionTo = async (): Promise<void> => {
    this.timedTransactions = [];
    this.isLoading = true;
    await this._transitionTo();
    this.isLoading = false;
  };
}
